import { ActionButton, Content, Dialog, Flex, Heading, Key, ProgressCircle, ToastQueue } from "@adobe/react-spectrum"
import { AIChatIcon } from "../../../../components/protected/Icons/IconsLib"
import styles from './add_update_product.module.css';
import { Button } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import { Item } from "../../../../services/soap/dataObjects/Item";
import { ProductDetailSection } from "./formSections/ProductDetailSection";
import { useTranslation } from "react-i18next";
import PricesSection from "./formSections/ProductPricesSection";
import ProductComponentSection from "./formSections/ProductComponentSection";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { CreateItemRequest } from "../../../../services/soap/item/requests/CreateItemRequest";
import { UpdateItemRequest } from "../../../../services/soap/item/requests/UpdateItemRequest";
import { GetNodeRequest } from "../../../../services/soap/tree/reqeusts/GetNodeRequest";

interface IAddUpdateProductProps {
    product: Item;
    isEdit: boolean;
    onClose: () => void;
    refreshDetailView: (data: any) => void;
    handleRefreshAddNewProduct: (data: any) => void;
}

const AddUpdateProduct: React.FC<IAddUpdateProductProps> = ({ product, isEdit, onClose, refreshDetailView, handleRefreshAddNewProduct }) => {
    const { t } = useTranslation('products');
    const _t = (key: string) => t(`addNewProduct.${key}`, { ns: 'products' });
    const _tt = (key: string) => t(`product_common.${key}`, { ns: 'products' });
    const { store, itemService, treeService } = useDependency();
    const [showLoader, setShowLoader] = useState(false);
    const [formData, setFormData] = useState<Item>({});
    const [validationErrors, setValidationErrors] = useState({});
    const [validationStatus, setValidationStatus] = useState({
        detailSection: false,
        pricesSection: true,
        componentSection: true,
        otherSection: true,
    });
    const [expandedSectionKey, setExpandedSectionKey] = useState<Set<Key>>(new Set(['detailSection']));
    const isFormValid = Object.values(validationStatus).every(Boolean);

    useEffect(() => {
        const raw = store?.session?.organization?.DEFAULTS;
        const normalizedDefaults: any[] = (() => {
            if (!raw) return [];
            if (Array.isArray(raw)) {
                const mapped = raw
                    .map((x: any) => (x?.DEFAULT !== undefined ? x.DEFAULT : x)) // peel DEFAULT if present
                    .filter(Boolean);
                return (Array.prototype.flat ? mapped.flat() : [].concat(...mapped));
            }

            if (typeof raw === 'object' && raw !== null) {
                const d = raw.DEFAULT;
                if (Array.isArray(d)) {
                    return d.filter(Boolean);
                }
                if (d) {
                    return [d];
                }
                return [];
            }

            return [];
        })();

        // Now it's safe: normalizedDefaults is a flat array of DEFAULT objects
        const node = normalizedDefaults.find((x: any) => x?.ident === 'PRODUCTSNODE');

        setFormData((prev: Item) => ({
            ...prev,
            // keep previous value if not found
            treeHandle: node?.node ?? ''
        }));
    }, []);

    useEffect(() => {
        if (!isEdit || !product) return;

        const compArray = Array.isArray(product.COMPONENT)
            ? product.COMPONENT
            : product.COMPONENT
                ? [product.COMPONENT]
                : [];

        const uniqueComponents = Array.from(
            new Map(compArray.map((item: any) => [item?.component, item])).values()
        );

        setFormData({
            ...product,
            COMPONENT: uniqueComponents,
        });
    }, [isEdit, product]);


    const handleSaveProduct = async () => {
        setShowLoader(true);
        if (!formData.id || formData.id === '') {
            const newProduct = await itemService.createItem(new CreateItemRequest(store.Server, store.SessionId, formData));
            if (newProduct.result == 'OK') {
                setShowLoader(false);
                const resp: any = newProduct;
                refreshDetailView(resp?.itemId);
                handleRefreshAddNewProduct(resp.itemId);
                onClose();
                ToastQueue.positive('Product created successfully:', { timeout: 3000, })
            } else {
                console.error('Error creating product:', newProduct);
                refreshDetailView(formData.id);
                onClose();
                ToastQueue.negative('Error creating product:', { timeout: 3000, })
                setShowLoader(false);
            }
        } else {
            const updatedProduct = await itemService.updateItem(new UpdateItemRequest(store.Server, store.SessionId, formData));
            if (updatedProduct.result == 'OK') {
                setShowLoader(false);
                ToastQueue.positive('Product updated successfully:', { timeout: 3000, })
                refreshDetailView(formData.id);
                onClose();
            } else {
                console.error('Error updating product:', updatedProduct);
                ToastQueue.negative('Error updating product:', { timeout: 3000, })
                setShowLoader(false);
            }
        }
    }

    return (
        <>
            <Dialog width={{ L: '1200px' }} aria-label="Add Product">
                <Heading marginStart={10} level={2}>
                    {_t(isEdit ? 'update_title' : 'add_title')}
                </Heading>
                <Content>
                    <ProductDetailSection
                        data={formData}
                        isEdit={isEdit}
                        onChange={setFormData}
                        validationErrors={validationErrors}
                        expandedSectionKey={expandedSectionKey}
                        setExpandedSectionKey={setExpandedSectionKey}
                        setValidationErrors={setValidationErrors}
                        fieldConfig={{}} // Assuming fieldConfig is passed here
                        validationStatus={validationStatus}
                        setValidationStatus={setValidationStatus} />

                    <PricesSection
                        data={formData}
                        onChange={setFormData}
                        validationErrors={validationErrors}
                        expandedSectionKey={expandedSectionKey}
                        setExpandedSectionKey={setExpandedSectionKey}
                        setValidationErrors={setValidationErrors}
                        fieldConfig={{}} // Assuming fieldConfig is passed here
                        validationStatus={validationStatus}
                        setValidationStatus={setValidationStatus} />
                    <ProductComponentSection
                        data={formData}
                        onChange={setFormData}
                        validationErrors={validationErrors}
                        expandedSectionKey={expandedSectionKey}
                        setExpandedSectionKey={setExpandedSectionKey}
                        setValidationErrors={setValidationErrors}
                        fieldConfig={{}} // Assuming fieldConfig is passed here
                        validationStatus={validationStatus}
                        setValidationStatus={setValidationStatus}
                        styles={styles} />
                </Content>
            </Dialog>
            <Flex
                UNSAFE_style={{
                    backgroundColor: 'white',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '1rem',
                    paddingLeft: 55,
                    paddingRight: 50,
                    boxShadow: '0 -1px 1px rgba(0,0,0,0.1)',
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1
                }}
            >
                <ActionButton isQuiet>
                    <AIChatIcon size={22}></AIChatIcon>
                </ActionButton>
                <Flex gap={'size-200'} UNSAFE_className={styles.button_container}>
                    <ProgressCircle aria-label='....Loading' isHidden={!showLoader} isIndeterminate UNSAFE_style={{ marginRight: 20 }} />
                    <Button variant='danger' onClick={onClose} className={`${styles.btn} ${styles.btn_cancel}`}>
                        {_tt('button_cancel')}
                    </Button>
                    <Button disabled={!isFormValid} type='submit' onClick={() => handleSaveProduct()} className={`${styles.btn} ${styles['btn-add']}`}>
                        {isEdit ? _tt("button_update") : _tt("button_save")}
                    </Button>
                </Flex>
            </Flex>
        </>
    )
}

export default AddUpdateProduct;
